import { isLegacyTier, isPermissionKey, normalizeScopeEntries, type ScopeEntry } from '@nexa/auth';
import type { AirportTree } from '@nexa/airport-reference';
import { http } from '@/lib/http';
import type {
  IAccessRole,
  ITeamDirectory,
  ITeamMemberRole,
  InviteInput,
  MemberStatus,
  RoleDraft,
  RoleUpdate,
  SetRolesInput,
  SetScopeInput,
  TeamDirectorySource,
  TeamMember,
} from './types';

// `operator-bff:/v1/team/*@v2` + `/v1/access/*@v1` (users-roles-permissions R4). One-shot
// reads seed TanStack Query; freshness comes from invalidation after our own writes and on
// `WS access.changed` (`./live.ts`) — never a poll (frontend-conventions §8.1).

/** Every team query lives under this prefix — invalidate it after any member write. */
export const TEAM_KEY = ['team'] as const;
export const MEMBERS_KEY = ['team', 'members'] as const;
export const memberKey = (operatorUrn: string) => ['team', 'member', operatorUrn] as const;
/** Not under `TEAM_KEY`: the directory is decided at the BFF's boot, member writes never change it. */
export const DIRECTORY_KEY = ['teamDirectory'] as const;
/** Every role query lives under this prefix. */
export const ROLES_KEY = ['access', 'roles'] as const;
export const roleQueryKey = (key: string) => ['access', 'roles', key] as const;

const STATUS_V2: Record<TeamMember['status'], MemberStatus> = {
  active: 'active',
  pending: 'pending',
  disabled: 'suspended',
};

function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback;
}

function rolesOf(raw: unknown): ITeamMemberRole[] {
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[]).flatMap((r) => {
    if (!r || typeof r !== 'object') return [];
    const { key, name, system } = r as Record<string, unknown>;
    if (typeof key !== 'string' || !key) return [];
    return [{ key, name: stringOr(name, key), system: system === true }];
  });
}

/**
 * Re-validate one team row (wire data). Tolerates a v1 row (an older BFF / the dev mock):
 * the v2 fields are derived where they can be and FAIL CLOSED where they cannot — an
 * unreadable scope is `[]` (no airports), never "the whole airline".
 */
export function normalizeMember(raw: unknown): TeamMember {
  const m = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const status: TeamMember['status'] =
    m.status === 'pending' || m.status === 'disabled' ? m.status : 'active';
  const memberStatus: MemberStatus =
    m.memberStatus === 'active' || m.memberStatus === 'pending' || m.memberStatus === 'suspended'
      ? m.memberStatus
      : STATUS_V2[status];
  const roles = rolesOf(m.roles);
  const roleKeys = Array.isArray(m.roleKeys)
    ? (m.roleKeys as unknown[]).filter((k): k is string => typeof k === 'string' && k !== '')
    : roles.map((r) => r.key);
  // v1 row: only the legacy single object exists — read it through the same fail-closed
  // normalizer (a malformed / multi-scope marker becomes `[]`).
  const scopes: ScopeEntry[] =
    normalizeScopeEntries(Array.isArray(m.scopes) ? m.scopes : m.scope) ?? [];
  const email = stringOr(m.email, '');
  return {
    operatorUrn: stringOr(m.operatorUrn, ''),
    email,
    displayName: stringOr(m.displayName, email),
    role: (m.role as TeamMember['role']) ?? null,
    scope: (m.scope as TeamMember['scope']) ?? null,
    lastLoginAt: typeof m.lastLoginAt === 'string' ? m.lastLoginAt : null,
    status,
    memberStatus,
    roleKeys,
    roles,
    scopes,
    accessVersion:
      typeof m.accessVersion === 'number' && Number.isFinite(m.accessVersion) ? m.accessVersion : 0,
    accessSource: m.accessSource === 'store' ? 'store' : 'none',
    invitationExpiresAt: typeof m.invitationExpiresAt === 'string' ? m.invitationExpiresAt : null,
  };
}

/** Re-validate one role (wire data): unknown permission keys are dropped. */
export function normalizeRole(raw: unknown): IAccessRole {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const key = stringOr(r.key, '');
  return {
    key,
    name: stringOr(r.name, key),
    ...(typeof r.description === 'string' && r.description ? { description: r.description } : {}),
    system: r.system === true,
    baseTier: isLegacyTier(r.baseTier) ? r.baseTier : 'finance_audit',
    permissions: Array.isArray(r.permissions)
      ? [...new Set((r.permissions as unknown[]).filter(isPermissionKey))]
      : [],
    assignedCount:
      typeof r.assignedCount === 'number' && Number.isFinite(r.assignedCount) ? r.assignedCount : 0,
    version: typeof r.version === 'number' && Number.isFinite(r.version) ? r.version : 0,
    ...(typeof r.updatedAt === 'string' ? { updatedAt: r.updatedAt } : {}),
    ...(typeof r.updatedBy === 'string' ? { updatedBy: r.updatedBy } : {}),
  };
}

const DIRECTORY_SOURCES: ReadonlySet<string> = new Set<TeamDirectorySource>(['auth0', 'synthetic', 'unconfigured']);

/**
 * Re-validate the directory flag (wire data). Unreadable (an older BFF, a malformed body) →
 * `null`: the screen then says nothing rather than guess. Only the real directory can claim
 * that invitations are sent.
 */
export function normalizeDirectory(raw: unknown): ITeamDirectory | null {
  const d = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (typeof d.source !== 'string' || !DIRECTORY_SOURCES.has(d.source)) return null;
  const source = d.source as TeamDirectorySource;
  return { source, invitationsSent: source === 'auth0' && d.invitationsSent === true };
}

const memberPath = (operatorUrn: string) => `v1/team/members/${encodeURIComponent(operatorUrn)}`;
const rolePath = (key: string) => `v1/access/roles/${encodeURIComponent(key)}`;

// ── /v1/team/* ─────────────────────────────────────────────────────────────

/** What the roster is on this deployment (real · synthetic · unconfigured). */
export async function getDirectory(): Promise<ITeamDirectory | null> {
  return normalizeDirectory(await http.get('v1/team/directory').json<unknown>());
}

export async function listMembers(): Promise<TeamMember[]> {
  const rows = await http.get('v1/team/members').json<unknown>();
  return Array.isArray(rows) ? rows.map(normalizeMember) : [];
}

export async function getMember(operatorUrn: string): Promise<TeamMember> {
  return normalizeMember(await http.get(memberPath(operatorUrn)).json<unknown>());
}

export async function inviteMember(input: InviteInput): Promise<TeamMember> {
  return normalizeMember(await http.post('v1/team/invite', { json: input }).json<unknown>());
}

export async function setMemberRoles(operatorUrn: string, input: SetRolesInput): Promise<TeamMember> {
  return normalizeMember(
    await http.put(`${memberPath(operatorUrn)}/roles`, { json: input }).json<unknown>(),
  );
}

export async function setMemberScope(operatorUrn: string, input: SetScopeInput): Promise<TeamMember> {
  return normalizeMember(
    await http.put(`${memberPath(operatorUrn)}/scope`, { json: input }).json<unknown>(),
  );
}

export async function suspendMember(operatorUrn: string): Promise<TeamMember> {
  return normalizeMember(await http.post(`${memberPath(operatorUrn)}/suspend`).json<unknown>());
}

export async function reactivateMember(operatorUrn: string): Promise<TeamMember> {
  return normalizeMember(await http.post(`${memberPath(operatorUrn)}/reactivate`).json<unknown>());
}

/** A pending invitee only. Returns the row of the NEW invitation (its URN changes). */
export async function resendInvitation(operatorUrn: string): Promise<TeamMember> {
  return normalizeMember(
    await http.post(`${memberPath(operatorUrn)}/invitation/resend`).json<unknown>(),
  );
}

export async function revokeInvitation(operatorUrn: string): Promise<void> {
  await http.delete(`${memberPath(operatorUrn)}/invitation`);
}

/** Tenant region → country → airport tree (NEX-308 proxy). */
export async function getAirportsTree(): Promise<AirportTree> {
  return http.get('v1/operator-context/airports-tree').json<AirportTree>();
}

// ── /v1/access/roles* ──────────────────────────────────────────────────────

export async function listRoles(): Promise<IAccessRole[]> {
  const body = await http.get('v1/access/roles').json<{ items?: unknown }>();
  return Array.isArray(body?.items) ? body.items.map(normalizeRole) : [];
}

export async function getRole(key: string): Promise<IAccessRole> {
  return normalizeRole(await http.get(rolePath(key)).json<unknown>());
}

export async function createRole(draft: RoleDraft): Promise<IAccessRole> {
  return normalizeRole(await http.post('v1/access/roles', { json: draft }).json<unknown>());
}

export async function updateRole(key: string, update: RoleUpdate): Promise<IAccessRole> {
  return normalizeRole(await http.put(rolePath(key), { json: update }).json<unknown>());
}

export async function deleteRole(key: string): Promise<void> {
  await http.delete(rolePath(key));
}
